import { useState, type ReactNode } from 'react';

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
}

interface Props<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: SegmentOption<T>[];
  className?: string;
  ariaLabel?: string;
  size?: 'sm' | 'md';
  /**
   * `upper` (default) renders 10px uppercase labels with `tracking-wider`, matching the
   * dashboard-spec spec. `normal` drops the uppercase + tracking and bumps to 12px medium —
   * used on the equity card where the reference shows mixed-case `Equity / Daily`.
   */
  case?: 'upper' | 'normal';
  /**
   * `true` = the Figma "Filter Selector" mode (Add-account broker picker): options stretch to
   * fill the track equally (`flex-grow: 1`), and the active pill is a single SLIDING thumb that
   * animates between options instead of each button painting its own background. Default false —
   * the compact hug-content look every existing call site keeps.
   */
  fill?: boolean;
  /**
   * `true` = a visible edge around the track in the dark theme (`.segmented-framed`, 2026-09-27: the day
   * panel's MANUAL / AI toggle, "put a proper frame around the manual and the AI"). Default false.
   */
  framed?: boolean;
}

/**
 * Pill-shaped segmented control matching the Figma spec:
 *  - track on `--canvas-inset` with 4px padding
 *  - active option is a `--canvas-raised` pill with the theme-aware card shadow — in `fill` mode
 *    it's ONE animated thumb sliding under equal-width options (2026-07-18 user direction)
 *  - inactive options render in `--text-muted` with hover lift + focus ring + active press
 *  - keyboard arrow-key navigation through the options (a11y)
 */
export function SegmentedControl<T extends string>({
  value, onChange, options, className = '', ariaLabel, size = 'md', case: textCase = 'upper', fill = false,
  framed = false,
}: Props<T>) {
  const padPx    = size === 'sm' ? 2 : 4;
  const pad      = size === 'sm' ? 'p-0.5' : 'p-1';
  const btnPad   = size === 'sm' ? 'px-3 py-1' : 'px-4 py-1.5';
  const typeClass = textCase === 'upper'
    ? 'text-[10px] font-medium uppercase tracking-wider'
    : 'text-[12px] font-medium';
  const activeIdx = options.findIndex(o => o.value === value);

  const onArrowKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const idx = options.findIndex(o => o.value === value);
    if (idx < 0) return;
    const next = e.key === 'ArrowRight'
      ? options[(idx + 1) % options.length]
      : options[(idx - 1 + options.length) % options.length];
    if (next) onChange(next.value);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onArrowKey}
      className={`relative inline-flex items-center rounded-lg ${pad} ${framed ? 'segmented-framed' : ''} ${className}`}
      style={{ background: 'var(--canvas-inset)' }}
    >
      {/* Sliding active-pill thumb (fill mode). Equal-width options → position is pure math, so the
          slide is a cheap CSS transform transition — no measurement, works through resizes. */}
      {fill && activeIdx >= 0 && (
        <div
          aria-hidden
          className="absolute rounded-md transition-transform duration-200 ease-out"
          style={{
            top: padPx,
            left: padPx,
            height: `calc(100% - ${padPx * 2}px)`,
            width: `calc((100% - ${padPx * 2}px) / ${options.length})`,
            transform: `translateX(${activeIdx * 100}%)`,
            background: 'var(--canvas-raised)',
            boxShadow: 'var(--shadow-card)',
          }}
        />
      )}
      {options.map(opt => (
        <SegmentButton
          key={opt.value}
          active={opt.value === value}
          fill={fill}
          onClick={() => onChange(opt.value)}
          btnPad={btnPad}
          typeClass={typeClass}
        >
          {opt.label}
        </SegmentButton>
      ))}
    </div>
  );
}

interface SegmentButtonProps {
  active: boolean;
  fill: boolean;
  onClick: () => void;
  btnPad: string;
  typeClass: string;
  children: ReactNode;
}

function SegmentButton({ active, fill, onClick, btnPad, typeClass, children }: SegmentButtonProps) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);

  // Inactive surface lift: subtle on hover, slightly stronger on press — both use the
  // canvas-raised tint via alpha so they read in both themes. In fill mode the ACTIVE pill is the
  // sliding thumb behind the buttons, so the button itself never paints an active background.
  const inactiveBg = pressed
    ? 'rgb(var(--canvas-raised-rgb) / 0.7)'
    : hover
      ? 'rgb(var(--canvas-raised-rgb) / 0.45)'
      : 'transparent';

  return (
    <button
      role="tab"
      aria-selected={active}
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className={`relative z-[1] cursor-pointer rounded-md transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
        fill ? 'flex-1 text-center' : ''
      } ${btnPad} ${typeClass} ${pressed ? 'scale-[0.97]' : ''}`}
      style={{
        background: active ? (fill ? 'transparent' : 'var(--canvas-raised)') : inactiveBg,
        color:      active ? 'var(--text)' : hover ? 'var(--text)' : 'var(--text-muted)',
        boxShadow:  active && !fill ? 'var(--shadow-card)' : 'none',
      }}
    >
      {children}
    </button>
  );
}
